import { ReactNode, useRef } from "react";
import useClickOutside from "@olivahealth/utils/reactHooks/useClickOutside";
import cx from "classnames";
import { ThreeDotsIcon } from "../../atoms/Icons";

interface OptionsMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  menuContent: ReactNode;
  menuPosition?: "bottom" | "left";
}

export default function OptionsMenu({
  isOpen,
  onOpenChange,
  menuContent,
  menuPosition = "bottom",
}: OptionsMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useClickOutside(wrapperRef, () => {
    if (isOpen) {
      onOpenChange(false);
    }
  });

  const getPositionStyles = () => {
    const gap = "8px";

    switch (menuPosition) {
      case "bottom":
        return {
          top: `calc(100% + ${gap})`,
          left: "60%",
          transform: "translateX(-60%)",
        };
      case "left":
        return {
          right: `calc(100% + ${gap})`,
          top: "0%",
          transform: "translateX(0%)",
        };
      default:
        return {
          top: `calc(100% + ${gap})`,
          left: "60%",
          transform: "translateX(-60%)",
        };
    }
  };

  const defaultTrigger = (
    <button
      onClick={() => onOpenChange(!isOpen)}
      className={cx(
        "w-10 h-10 flex items-center justify-center rounded-lg transition-colors",
        isOpen
          ? "bg-neutral-900"
          : "hover:bg-neutral-800/50 active:bg-neutral-900",
      )}
      aria-expanded={isOpen}
    >
      <ThreeDotsIcon size={24} color="white" />
    </button>
  );

  return (
    <div
      ref={wrapperRef}
      className="relative flex items-center align-middle z-0"
      style={{ zIndex: isOpen ? 50 : 0 }}
    >
      {defaultTrigger}
      <div
        className={cx("absolute", isOpen ? "" : "hidden")}
        style={{
          width: "256px",
          ...getPositionStyles(),
        }}
      >
        {menuContent}
      </div>
    </div>
  );
}
